// ============================================================
// Sağlık — ilaç takibi ve olay kayıtları (§5)
//
// İki kural bu modülün varlık sebebidir:
//   1. Çocuğa velinin yazılı izni olmadan ilaç verilemez.
//   2. Verildiyse kim, ne zaman, hangi dozu verdi kayıtlı olmalı.
// Kaza/yaralanma tutanağı da aynı gerekçeyle yapısal tutulur.
// ============================================================

import { BUGUN } from "../config.js";
import { OLAY_TURU } from "../data/tanimlar.js";
import { B, staffOf, stu, subeBulHerYerde } from "./context.js";

export { OLAY_TURU };


const sube = branchId =>
  (branchId ? subeBulHerYerde(branchId) : B());

// ---------- İlaçlar ----------

export const ilaclar = branchId => sube(branchId).medications ?? [];

/** Bugün için geçerli ilaç kayıtları — süresi dolmuş reçeteler düşer. */
export function aktifIlaclar(branchId) {
  return ilaclar(branchId).filter(m => !m.endDate || m.endDate >= BUGUN);
}

export const ogrencininIlaclari = (studentId, branchId) =>
  ilaclar(branchId).filter(m => m.studentId === studentId);

/** Acil müdahale ilaçları — EpiPen gibi, konumu herkesçe bilinmeli. */
export const acilIlaclar = branchId =>
  aktifIlaclar(branchId).filter(m => m.schedule === "acil");

/**
 * İlacın kendi son kullanma tarihi. EpiPen'in süresi dolmuşsa
 * anafilaksi anında elde işe yaramaz bir enjektör kalır.
 */
export function ilacSuresi(med, bugun = BUGUN) {
  if (!med.expiresAt) return { durum: "bilinmiyor", kalanGun: null };
  const kalanGun = Math.round(
    (new Date(med.expiresAt + "T00:00:00Z") - new Date(bugun + "T00:00:00Z")) / 86400000,
  );
  const durum = kalanGun < 0 ? "doldu" : kalanGun <= 60 ? "yaklasiyor" : "gecerli";
  return { durum, kalanGun };
}

export const suresiSorunluIlaclar = branchId =>
  aktifIlaclar(branchId).filter(m => ilacSuresi(m).durum !== "gecerli");

/** Bugün uygulanan dozlar. */
export const bugunkuUygulamalar = branchId =>
  aktifIlaclar(branchId).flatMap(m =>
    (m.administrations ?? [])
      .filter(u => u.at.startsWith(BUGUN))
      .map(u => ({ ...u, med: m, student: stu(m.studentId) })));

/** Veli izni eksik ilaç — verilmemeli. */
export const izinsizIlaclar = branchId =>
  aktifIlaclar(branchId).filter(m => !m.consent?.at);

// ---------- Olaylar ----------

export const olaylar = branchId =>
  [...(sube(branchId).incidents ?? [])].sort((a, b) => b.at.localeCompare(a.at));

export const ogrencininOlaylari = (studentId, branchId) =>
  olaylar(branchId).filter(o => o.studentId === studentId);

/** Veliye bildirilmemiş olaylar — bekletilmemeli. */
export const bildirilmemisOlaylar = branchId =>
  olaylar(branchId).filter(o => !o.parentNotifiedAt);

/** Veli okudu onayı bekleyenler. */
export const onaysizOlaylar = branchId =>
  olaylar(branchId).filter(o => o.parentNotifiedAt && !o.parentAckAt);

export const bugunkuOlaylar = branchId =>
  olaylar(branchId).filter(o => o.at.startsWith(BUGUN));

/** Son N gündeki olaylar. */
export function sonOlaylar(branchId, gun = 30) {
  const esik = new Date(new Date(BUGUN + "T00:00:00Z") - gun * 86400000)
    .toISOString().slice(0, 10);
  return olaylar(branchId).filter(o => o.at.slice(0, 10) >= esik);
}

export const olayPersoneli = o => staffOf(o.staffId)?.name ?? "—";

/**
 * Şubenin sağlık durumu özeti — panelde ve sağlık ekranında kullanılır.
 * Sayıların hepsi "bir şey yapılması gerekiyor" anlamına gelir.
 */
export function saglikOzeti(branchId) {
  const t = sube(branchId);
  return {
    acilIlac: acilIlaclar(branchId).length,
    suresiSorunlu: suresiSorunluIlaclar(branchId).length,
    izinsiz: izinsizIlaclar(branchId).length,
    bugunUygulama: bugunkuUygulamalar(branchId).length,
    bugunOlay: bugunkuOlaylar(branchId).length,
    bildirilmemis: bildirilmemisOlaylar(branchId).length,
    onaysiz: onaysizOlaylar(branchId).length,
    toplamOgrenci: t.students.length,
  };
}
